// The trigger editor: Now / Once / Repeat / On an event / Watch.
//
// The backend's triggers.validate() is strict (triggerRules.js holds its rules), so the editor only
// ever hands onChange what it reads:
//  - numbers are whole, and inside their control's range before onChange hears of them;
//  - the weekdays and the confirmation replies are never an empty list (the backend reads an empty
//    list as "every day" / "every reply", the opposite of unticking the last one), so the last one
//    ticked can't be unticked;
//  - this_patient and repeat are booleans, statuses are C, R and U.
// The last day and the last reply stay focusable (aria-disabled, not disabled), with a note beside
// them that says why, so a keyboard, a screen reader and a touch screen all learn it.
// A draft that is still being filled in (a time cleared, a watch with no question yet) does reach
// onChange, because the field shows it. draftHint() names what is missing in a few words: the
// line under the controls shows it, and the plan card holds Deploy back while there is one.
// describeTrigger() says "Schedule can't be read" for such a draft, so that sentence is never shown.
import React, { useId, useState } from 'react';
import { ALL_DAYS, DAY_NAMES, EVENTS, describeTrigger } from './triggerText';
import { KINDS, LIMITS, PICK_WHEN, STATUS_CODES, draftHint, isObject, statusOf, text, watchChecks, whole } from './triggerRules';

const FIELD = 'rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-800 dark:text-slate-100';
const LABEL = 'text-[11px] font-semibold text-slate-500 dark:text-slate-400';
const NOTE = 'text-[11px] text-slate-600 dark:text-slate-400';
const WARN = 'text-[11px] text-amber-700 dark:text-amber-300';
const CHIP = 'px-2 py-0.5 rounded-md text-[11px] font-semibold';
const CHIP_ON = 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300';
const CHIP_OFF = 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400';
const LOCKED = 'opacity-60 cursor-not-allowed';

const WATCH_INTERVALS = [[15, '15 min'], [30, '30 min'], [60, '1 h'], [120, '2 h'], [240, '4 h'], [480, '8 h'], [1440, '24 h']];
// C is confirmed, R asked to reschedule, U asked and no answer yet. Not "unclear".
const REPLIES = [['C', 'Confirmed'], ['R', 'Asked to reschedule'], ['U', 'Asked, no answer yet']];

const clamp = (number, low, high) => Math.min(high, Math.max(low, number));

// ---- small helpers ----------------------------------------------------------------------------

// Tomorrow's date in `zone` (the plan's time zone; this browser's when there is none) as
// YYYY-MM-DD. Calendar arithmetic, not "now plus 24 hours", which names the wrong date on the two
// nights a year the clocks change.
function tomorrow(zone) {
  const now = new Date();
  let [year, month, day] = [now.getFullYear(), now.getMonth() + 1, now.getDate()];
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone || undefined, year: 'numeric', month: 'numeric', day: 'numeric' })
      .formatToParts(now);
    const part = (type) => Number(parts.find((p) => p.type === type).value);
    [year, month, day] = [part('year'), part('month'), part('day')];
  } catch {
    // a name that isn't a time zone: this browser's date stands
  }
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  const pad = (number, size) => String(number).padStart(size, '0');
  return `${pad(next.getUTCFullYear(), 4)}-${pad(next.getUTCMonth() + 1, 2)}-${pad(next.getUTCDate(), 2)}`;
}

const DEFAULTS = {
  now: () => ({ kind: 'now' }),
  once: (zone) => ({ kind: 'once', date: tomorrow(zone), time: '08:00' }),
  repeat: () => ({ kind: 'repeat', days: [0, 1, 2, 3, 4], time: '07:00' }),
  event: () => ({ kind: 'event', event: 'new_fax' }),
  watch: () => ({
    kind: 'watch', check: '', every_minutes: LIMITS.watch_default_every_minutes, days: LIMITS.watch_default_days, repeat: false,
  }),
};

// The weekdays shown ticked: the list's valid, distinct days; none named means every day, as the
// backend reads it.
function tickedDays(raw) {
  const days = Array.isArray(raw) ? [...new Set(raw.filter((d) => whole(d) && d >= 0 && d <= 6))].sort((a, b) => a - b) : [];
  return days.length ? days : ALL_DAYS;
}

// The confirmation replies shown ticked, in C, R, U order; none named means all three.
function tickedReplies(raw) {
  const named = Array.isArray(raw) ? raw.map(statusOf) : [];
  const ticked = STATUS_CODES.filter((code) => named.includes(code));
  return ticked.length ? ticked : STATUS_CODES;
}

function minutesLabel(minutes) {
  return minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`;
}

// A number the person typed as a whole number, or null when there is none yet (empty, or not a
// number). Rounded, never cut: 1.5 is never sent.
function typedWhole(raw) {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const number = Math.round(Number(raw));
  return Number.isFinite(number) ? number : null;
}

// A whole-number field. onCommit gets a whole number inside min..max as soon as one is typed. The
// field shows what is typed while that agrees with the committed value or is empty (so 15 can be
// typed on the way through 1, and the field can be cleared), and the committed value after that
// and on blur. An empty field commits nothing: a missing number would be read as "not set".
function WholeInput({ label, value, range: [min, max], onCommit, className = '' }) {
  const [draft, setDraft] = useState(null);
  const typed = draft === null ? null : typedWhole(draft);
  const agrees = typed === null || (typed <= max && clamp(typed, min, max) === value);
  return (
    <input aria-label={label} type="number" min={min} max={max} step={1} className={`${FIELD} ${className}`}
           value={draft !== null && agrees ? draft : String(value)}
           onChange={(e) => {
             setDraft(e.target.value);
             const number = typedWhole(e.target.value);
             if (number !== null) onCommit(clamp(number, min, max));
           }}
           onBlur={() => setDraft(null)} />
  );
}

// What the editor shows for `value`. null, undefined and {} are "now" (the backend's first rule, and
// draftHint's). A value with no kind the backend knows (no kind, an empty or null one, a typo, or
// not an object at all) shows nothing picked: the "Pick when it runs" that draftHint, and so
// Deploy, says for it, never a "Now" that would not be sent.
const NOTHING_PICKED = { kind: '' };
function shown(value) {
  if (value === null || value === undefined || (isObject(value) && Object.keys(value).length === 0)) return DEFAULTS.now();
  return isObject(value) ? value : NOTHING_PICKED;
}

// ---- the editor -------------------------------------------------------------------------------

// `timezone` is the plan's IANA zone (tomorrow's date for a new one-off is read in it). `allowNow`
// is false for an agent being edited: the server answers a "now" trigger there with "use Run now".
export default function TriggerEditor({ value, onChange, hasPatient = false, timezone = null, allowNow = true }) {
  const t = shown(value);
  const set = (patch) => onChange({ ...t, ...patch });
  const daysNote = useId();
  const repliesNote = useId();
  const hint = draftHint(t);
  const hoursMode = t.kind === 'repeat' && t.every_hours != null;
  const days = tickedDays(t.days);
  const replies = tickedReplies(t.statuses);
  const knownEvent = EVENTS.some(([key]) => key === t.event);
  const checks = t.kind === 'watch' ? watchChecks(t) : 0;
  const intervals = whole(t.every_minutes) && !WATCH_INTERVALS.some(([minutes]) => minutes === t.every_minutes)
    ? [...WATCH_INTERVALS, [t.every_minutes, minutesLabel(t.every_minutes)]].sort((a, b) => a[0] - b[0])
    : WATCH_INTERVALS;

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2">
        <span className={LABEL}>When</span>
        <select className={FIELD} value={KINDS.includes(t.kind) ? t.kind : ''}
                onChange={(e) => onChange(DEFAULTS[e.target.value](timezone))}>
          {!KINDS.includes(t.kind) && <option value="" disabled>{PICK_WHEN}</option>}
          {(allowNow || t.kind === 'now') && <option value="now">Now</option>}
          <option value="once">Once</option>
          <option value="repeat">Repeat</option>
          <option value="event">On an event</option>
          <option value="watch">Watch for something</option>
        </select>
      </label>

      {t.kind === 'once' && (
        <div className="flex gap-2">
          <input aria-label="Date" type="date" className={FIELD} value={text(t.date)} onChange={(e) => set({ date: e.target.value })} />
          <input aria-label="Time" type="time" className={FIELD} value={text(t.time)} onChange={(e) => set({ time: e.target.value })} />
        </div>
      )}

      {t.kind === 'repeat' && (
        <div className="space-y-2">
          {hoursMode ? (
            <label className="flex items-center gap-2">
              <span className={LABEL}>Every</span>
              <WholeInput label="Hours" range={LIMITS.hours} className="w-16" value={t.every_hours}
                          onCommit={(hours) => onChange({ kind: 'repeat', every_hours: hours })} />
              <span className={LABEL}>hours</span>
            </label>
          ) : (
            <>
              <div role="group" aria-label="Days of the week" className="flex flex-wrap gap-1">
                {DAY_NAMES.map((name, day) => {
                  const on = days.includes(day);
                  const last = on && days.length === 1;
                  return (
                    <button key={name} type="button" aria-pressed={on} aria-disabled={last || undefined}
                            aria-describedby={last ? daysNote : undefined}
                            onClick={() => {
                              const next = on ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b);
                              if (next.length) set({ days: next });
                            }}
                            className={`${CHIP} ${on ? CHIP_ON : CHIP_OFF} ${last ? LOCKED : ''}`}>
                      {name}
                    </button>
                  );
                })}
              </div>
              {days.length === 1 && <div id={daysNote} className={NOTE}>At least one day stays ticked.</div>}
              <input aria-label="Time" type="time" className={FIELD} value={text(t.time)} onChange={(e) => set({ time: e.target.value })} />
            </>
          )}
          <label className={`flex items-center gap-1.5 ${NOTE}`}>
            <input type="checkbox" checked={hoursMode}
                   onChange={(e) => onChange(e.target.checked ? { kind: 'repeat', every_hours: 4 } : DEFAULTS.repeat())} />
            Every few hours instead
          </label>
        </div>
      )}

      {t.kind === 'event' && (
        <div className="space-y-2">
          <select aria-label="Event" className={FIELD} value={knownEvent ? t.event : ''}
                  onChange={(e) => onChange({ kind: 'event', event: e.target.value })}>
            {!knownEvent && <option value="" disabled>Pick an event</option>}
            {EVENTS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          {t.event === 'visit_soon' && (
            <label className="flex items-center gap-2">
              <WholeInput label="Minutes before" range={LIMITS.visit_minutes} className="w-20"
                          value={t.minutes ?? LIMITS.visit_default_minutes} onCommit={(minutes) => set({ minutes })} />
              <span className={LABEL}>minutes before</span>
            </label>
          )}
          {t.event === 'confirmation' && (
            <>
              <div role="group" aria-label="Replies that start the agent" className={`flex flex-wrap gap-3 ${NOTE}`}>
                {REPLIES.map(([code, label]) => {
                  const on = replies.includes(code);
                  const last = on && replies.length === 1;
                  return (
                    <label key={code} className={`flex items-center gap-1 ${last ? LOCKED : ''}`}>
                      <input type="checkbox" checked={on} aria-disabled={last || undefined}
                             aria-describedby={last ? repliesNote : undefined}
                             onChange={(e) => {
                               const next = STATUS_CODES.filter((c) => (c === code ? e.target.checked : replies.includes(c)));
                               if (next.length) set({ statuses: next });
                             }} />
                      {label}
                    </label>
                  );
                })}
              </div>
              {replies.length === 1 && <div id={repliesNote} className={NOTE}>At least one reply stays ticked.</div>}
            </>
          )}
          {t.event === 'new_report' && (hasPatient || t.this_patient === true) && (
            <label className={`flex items-center gap-1.5 ${NOTE}`}>
              <input type="checkbox" checked={t.this_patient === true}
                     onChange={(e) => onChange(e.target.checked ? { kind: 'event', event: 'new_report', this_patient: true }
                                                                : { kind: 'event', event: 'new_report' })} />
              Only for this patient
            </label>
          )}
        </div>
      )}

      {t.kind === 'watch' && (
        <div className="space-y-2">
          <input aria-label="What to watch for" className={`${FIELD} w-full`} maxLength={LIMITS.max_check_length}
                 placeholder="A yes/no question, e.g. Has the MRI report been filed?"
                 value={text(t.check)} onChange={(e) => set({ check: e.target.value })} />
          <div className="flex items-center gap-2 flex-wrap">
            <span className={LABEL}>Check every</span>
            <select aria-label="Check every" className={FIELD} value={t.every_minutes ?? LIMITS.watch_default_every_minutes}
                    onChange={(e) => set({ every_minutes: Number(e.target.value) })}>
              {intervals.map(([minutes, label]) => <option key={minutes} value={minutes}>{label}</option>)}
            </select>
            <span className={LABEL}>for</span>
            <WholeInput label="Days" range={LIMITS.watch_days} className="w-16"
                        value={t.days ?? LIMITS.watch_default_days} onCommit={(count) => set({ days: count })} />
            <span className={LABEL}>days</span>
          </div>
          <label className={`flex items-center gap-1.5 ${NOTE}`}>
            <input type="checkbox" checked={t.repeat === true} onChange={(e) => set({ repeat: e.target.checked })} />
            Keep watching after it happens
          </label>
          {checks > 0 && <div className={NOTE}>About {checks} {checks === 1 ? 'check' : 'checks'}.</div>}
        </div>
      )}

      <div role="status" aria-label="Schedule in words" className={NOTE}>
        {hint ? <span className={WARN}>{hint}</span> : <><span>Runs: </span><span>{describeTrigger(t)}</span></>}
      </div>
    </div>
  );
}
